import { ErrorCode } from './code/error-code';
import { EncodingUtil } from './utils/encoding-util';

const AtlAgentTypeEnum = {
  PK11: 'PK11',
  CAPI: 'CAPI',
  ATM: 'ATM'
};

export class TwcaAtlAgentLib {
  capiAgent = null;
  pk11Agent = null;
  atmAgent = null;

  constructor(beginPort=49100, detectVersion_WIN=["1.1.0.0"], detectVersion_MAC=["1.1.0.0"]) {
    if (this.pk11Agent == null) {
      this.pk11Agent = initPk11AgentForChromeInIDPaaS(beginPort, detectVersion_WIN, detectVersion_MAC);
    }
    if (this.capiAgent == null) {
      this.capiAgent = initCapiAgentForChromeInIDPaaS(beginPort, detectVersion_WIN, detectVersion_MAC);
    }
    if (this.atmAgent == null) {
      this.atmAgent = initATMAgentForChromeInIDPaaS(beginPort, detectVersion_WIN, detectVersion_MAC);
    }
  }

  /**
   * 用來回傳指定的中介元件。
   *
   * @private
   * @param type ATL Agent 類型
   * @returns {Object} ATL Agent 物件
   */
  #getAgent(type) {
    switch (type) {
      case AtlAgentTypeEnum.PK11:
        return this.pk11Agent;
      case AtlAgentTypeEnum.CAPI:
        return this.capiAgent;
      case AtlAgentTypeEnum.ATM:
        return this.atmAgent;
      default:
        return null;
    }
  }

  /**
   * 回傳錯誤代碼與訊息的追蹤物件，通常放到 reserved 供檢查使用。
   *
   * @private
   * @param type ATL Agent 類型
   * @returns {Object} 追蹤物件
   */
  #getAgentTraceObj(type) {
    return {
      agentCode: this.#getAgent(type).GetErrorCode(),
      agentMsg: this.#getAgent(type).GetErrorMsg()
    };
  }

  /**
   * 檢查用戶端中介元件是否載入，若存在則產生元件雙向認證所需參數。
   *
   * @private
   * @param type ATL Agent 類型
   * @param cbf  callback(code, msg, reserved, data)
   * @returns {boolean} 是否成功
   */
  #agentCheck(type, cbf) {
    let useAgent = this.#getAgent(type);

    // 初始化元件
    useAgent.initChromeAsync((initRes) => {
      if (initRes !== 0) {
        cbf('1182000', ErrorCode['1182000'], null, {});
        return false;
      }

      // 取得雙向認證所需參數
      let clientAuthCode = '';
      if (type === AtlAgentTypeEnum.ATM) {
        let serverAuthRet = useAgent.GetServerAuth();
        if (serverAuthRet != 0) {
          cbf('1186000', ErrorCode['1186000'], null, {});
          return false;
        }
        clientAuthCode = useAgent.GetServerCode();
        if (clientAuthCode === null) {
          cbf('1186000', ErrorCode['1186000'], null, {});
          return false;
        }
      } else {
        clientAuthCode = useAgent.GetServerAuth();
        if (clientAuthCode === null) {
          cbf('1186000', ErrorCode['1186000'], null, {});
          return false;
        }
      }

      // 回傳成功結果
      let data = {
        clientAuthCode: clientAuthCode
      };
      cbf('0', ErrorCode['0'], null, data);
      return true;
    });
  }

  /**
   * 以中介元件驗證伺服器端的認證碼。
   *
   * @private
   * @param type           ATL Agent 類型
   * @param serverAuthCode 伺服器認證碼
   * @param cbf            callback(code, msg, reserved, data)
   * @returns {boolean} 是否成功
   */
  #agentServerAuth(type, serverAuthCode, cbf) {
    let useAgent = this.#getAgent(type);

    // 檢查參數
    if (!serverAuthCode) {
      cbf('1185005', ErrorCode['1185005'], null, {});
      return false;
    }

    // 驗證
    let ret = useAgent.VerifyServerResponse(serverAuthCode);
    if (ret != 0) {
      cbf('1186001', ErrorCode['1186001'], null, {});
      return false;
    }

    // 初始化部分設定
    if (type === AtlAgentTypeEnum.PK11) {
      useAgent.SetTokenType(2);
    }

    // 回傳成功結果
    cbf('0', ErrorCode['0'], null, {});
    return true;
  }

  /**
   * [PK11] 檢查用戶端中介元件是否載入，若存在則產生元件雙向認證所需參數。
   *
   * @param cbf callback(code, msg, reserved, data)
   */
  pk11Check(cbf) {
    this.#agentCheck(AtlAgentTypeEnum.PK11, cbf);
  }

  /**
   * [PK11] 以中介元件驗證伺服器端的認證碼。
   *
   * @param serverAuthCode 伺服器認證碼
   * @param cbf            callback(code, msg, reserved, data)
   */
  pk11ServerAuth(serverAuthCode, cbf) {
    this.#agentServerAuth(AtlAgentTypeEnum.PK11, serverAuthCode, cbf);
  }

  /**
   * [PK11] 登入載具選擇憑證
   *
   * @private
   */
  #pk11Login(pwd, cbf) {
    // 檢查參數
    if (!pwd) {
      cbf('1185005', ErrorCode['1185005'], null, {});
      return false;
    }

    // 檢查卡片狀態
    let cardStatus = this.pk11Agent.GetCardStatus();
    if (cardStatus !== 0) {
      cbf('1186100', ErrorCode['1186100'], null, {});
      return false;
    }

    // 登入
    let loginRes = this.pk11Agent.Login(pwd);
    if (loginRes === 36896) {
      cbf('1186101', ErrorCode['1186101'], null, {});
      return false;
    } else if (loginRes !== 0) {
      cbf('1186102', ErrorCode['1186102'], this.#getAgentTraceObj(AtlAgentTypeEnum.PK11), {});
      return false;
    }

    // 挑憑證
    let selectRes = this.pk11Agent.SelectCert(1);
    if (selectRes !== 0) {
      cbf('1186103', ErrorCode['1186103'], this.#getAgentTraceObj(AtlAgentTypeEnum.PK11), {});
      return false;
    }

    return true;
  }

  /**
   * [PK11] 挑選完憑證後，直接透過 PK11 取得憑證相關資訊。
   *
   * @private
   */
  #pk11ParseCard() {
    // 取得挑到的憑證
    let b64Cert = this.pk11Agent.GetSelectedCertB64();
    let subject = this.pk11Agent.GetSelectedCertSubject();
    let cardType = this.pk11Agent.GetCardType();            // 回傳 1: GPKI, 2: XML, 5: HCA

    let cardName;
    let id;
    if (cardType == '1') {
      cardName = this.pk11Agent.GetHiCertCaName();

      if (cardName == 'MOEACA') {
        id = this.pk11Agent.GetHiCertUniformOrganizationID();
      } else if (cardName == 'XCA' || cardName == 'GCA') {
        id = this.pk11Agent.GetHiCertEntityOID();
      }
    } else if (cardType == '2') {
      cardName = 'XML';
    } else if (cardType == '5') {
      cardName = 'HCA';

      this.pk11Agent.ReadHCAData();
      let hcaDataStr = this.pk11Agent.GetHCAData();
      let hcaData = JSON.parse(hcaDataStr);
      if (hcaData['CardType'] === 1) {
        // 醫事人員卡
        id = hcaData['sPersID'];
      } else if (hcaData['CardType'] === 2) {
        // 醫事機構卡
        id = hcaData['sHicID'];
      }
    }

    // 回傳成功結果
    let data = {
      b64Cert: b64Cert,
      subject: subject,
      cardType: cardName,
    };
    if (id) {
      data['id'] = id;
    }
    return data;
  }

  /**
   * [PK11] 簽章流程
   *
   * @private
   */
  #pk11Sign(signType, cts, cbf) {
    // 檢查參數
    if (!cts) {
      cbf('1185005', ErrorCode['1185005'], null, {});
      return false;
    }

    // 簽署
    let signRes = -1;
    switch (signType) {
      case 'PKCS1_HASHED':
        this.pk11Agent.SetEncoding(0);
        signRes = this.pk11Agent.SignPkcs1Hashed(cts);
        break;
      case 'PKCS1':
        this.pk11Agent.SetEncoding(0);
        signRes = this.pk11Agent.SignPKCS1(cts);
        break;
      case 'PKCS7':
        this.pk11Agent.SetEncoding(0);
        signRes = this.pk11Agent.SignPKCS7(cts);
        break;
      case 'PKCS7UTF8':
        this.pk11Agent.SetEncoding(1);
        signRes = this.pk11Agent.SignPKCS7(cts);
        this.pk11Agent.SetEncoding(0);
        break;
      default:
        cbf('1185005', ErrorCode['1185005'], null, {});
        return false;
    }
    if (signRes !== 0) {
      cbf('1186104', ErrorCode['1186104'], this.#getAgentTraceObj(AtlAgentTypeEnum.PK11), {});
      return false;
    }

    // 取得簽章結果
    let signature = '';
    switch (signType) {
      case 'PKCS1_HASHED':
      case 'PKCS1':
        signature = this.pk11Agent.GetPKCS1Data();
        break;
      case 'PKCS7':
      case 'PKCS7UTF8':
        signature = this.pk11Agent.GetPKCS7Data();
        break;
      default:
        cbf('1185005', ErrorCode['1185005'], null, {});
        return false;
    }

    // 先取得憑證相關資訊
    let data = this.#pk11ParseCard();
    // 附加簽章結果
    data['signature'] = signature;

    cbf('0', ErrorCode['0'], null, data);
    return true;
  }

  /**
   * [PK11] 回傳憑證卡片中的憑證值及相關資訊。
   *
   * @param cbf callback(code, msg, reserved, data)
   */
  pk11SelectCert(cbf) {
    // 檢查卡片狀態
    let cardStatus = this.pk11Agent.GetCardStatus();
    if (cardStatus !== 0) {
      cbf('1186100', ErrorCode['1186100'], null, {});
      return false;
    }

    // 挑憑證
    let selectRes = this.pk11Agent.SelectCert(1);
    if (selectRes !== 0) {
      cbf('1186103', ErrorCode['1186103'], this.#getAgentTraceObj(AtlAgentTypeEnum.PK11), {});
      return false;
    }

    // 取得挑到的憑證，並回傳成功結果
    let data = this.#pk11ParseCard();
    cbf('0', ErrorCode['0'], null, data);
  }

  /**
   * [PK11] 以憑證卡片簽署 PKCS1 資料，請輸入已經雜湊過的資料。
   *
   * @param pwd  卡片密碼
   * @param hash 簽章本文 hash 值
   * @param cbf callback(code, msg, reserved, data)
   */
  pk11SignPkcs1Hashed(pwd, hash, cbf) {
    // 解析 hash 為待簽章資料
    let cts = EncodingUtil.hexToBase64(hash);

    // 登入挑憑證
    if (!this.#pk11Login(pwd, cbf))
      return;

    // 簽署
    this.#pk11Sign('PKCS1_HASHED', cts, cbf);
    this.pk11Agent.Logout();
  }

  /**
   * [PK11] 以憑證卡片簽署 PKCS7 資料。
   *
   * @param pwd 卡片密碼
   * @param cts 待簽章明文
   * @param cbf callback(code, msg, reserved, data)
   */
  pk11SignPkcs7(pwd, cts, cbf) {
    // 登入挑憑證
    if (!this.#pk11Login(pwd, cbf))
      return;

    // 簽署
    this.#pk11Sign('PKCS7', cts, cbf);
    this.pk11Agent.Logout();
  }

    /**
   * [PK11] 以憑證卡片簽署 PKCS7 資料。
   *
   * @param pwd 卡片密碼
   * @param cts 待簽章明文
   * @param cbf callback(code, msg, reserved, data)
   */
    pk11SignPkcs7Utf8(pwd, cts, cbf) {
      // 登入挑憑證
      if (!this.#pk11Login(pwd, cbf))
        return;

      // 簽署
      this.#pk11Sign('PKCS7UTF8', cts, cbf);
      this.pk11Agent.Logout();
    }

  /**
   * [CAPI] 檢查用戶端中介元件是否載入，若存在則產生元件雙向認證所需參數。
   *
   * @param cbf callback(code, msg, reserved, data)
   */
  capiCheck(cbf) {
    this.#agentCheck(AtlAgentTypeEnum.CAPI, cbf);
  }

  /**
   * [CAPI] 以中介元件驗證伺服器端的認證碼。
   *
   * @param serverAuthCode 伺服器認證碼
   * @param cbf            callback(code, msg, reserved, data)
   */
  capiServerAuth(serverAuthCode, cbf) {
    this.#agentServerAuth(AtlAgentTypeEnum.CAPI, serverAuthCode, cbf);
  }

  /**
   * [CAPI] 挑選完憑證後，直接透過 CAPI 取得憑證相關資訊。
   */
  #capiParsePfx() {
    return {
      b64Cert: this.capiAgent.GetCertB64(),
      subject: this.capiAgent.GetCertSubject()
    };
  }

  /**
   * [CAPI] 簽章流程
   *
   * @private
   */
  #capiSign(signType, certFilter, pwd, cts, cbf) {
    // 檢查參數
    if (!certFilter || !cts) {
      cbf('1185005', ErrorCode['1185005'], null, {});
      return false;
    }

    // 挑選憑證
    let selectRes = this.capiAgent.SelectSignerEx(certFilter, '', '', pwd, '', 0x0088, 0);
    if (selectRes !== 0) {
      cbf('1186103', ErrorCode['1186103'], this.#getAgentTraceObj(AtlAgentTypeEnum.CAPI), {});
      return false;
    }

    // 簽署
    let signature = '';
    switch (signType) {
      case 'PKCS1_HASHED':
        signature = this.capiAgent.SignPkcs1Hashed(cts, 0x0100);
        break;
      case 'PKCS1':
        signature = this.capiAgent.SignPkcs1(cts);
        break;
      case 'PKCS7':
        signature = this.capiAgent.SignPkcs7(cts);
        break;
      case 'PKCS7UTF8':
        signature = this.capiAgent.SignPkcs7(cts, 0x0800);
        break;
      default:
        cbf('1185005', ErrorCode['1185005'], null, {});
        return false;
    }
    if (!signature) {
      cbf('1186104', ErrorCode['1186104'], this.#getAgentTraceObj(AtlAgentTypeEnum.PK11), {});
      return false;
    }

    // 先取得憑證相關資訊
    let data = this.#capiParsePfx();
    // 附加簽章結果
    data['signature'] = signature;

    cbf('0', ErrorCode['0'], null, data);
    return true;
  }

  /**
   * [CAPI] 回傳軟體憑證中的憑證值及相關資訊。
   *
   * @param {*} certFilter
   * @param {*} pwd
   * @param {*} cbf
   */
  capiSelectCert(certFilter, pwd, cbf) {
    // 檢查參數
    if (!certFilter) {
      cbf('1185005', ErrorCode['1185005'], null, {});
      return;
    }

    // 挑選憑證
    let selectRes = this.capiAgent.SelectSignerEx(certFilter, '', '', pwd, '', 0x0808, 0);
    if (selectRes !== 0) {
      cbf('1186103', ErrorCode['1186103'], this.#getAgentTraceObj(AtlAgentTypeEnum.CAPI), {});
      return;
    }

    // 取得挑到的憑證，並回傳成功結果
    let data = this.#capiParsePfx();
    cbf('0', ErrorCode['0'], null, data);
  }

  /**
   * [CAPI] 以用戶端憑軟體憑證簽署 PKCS1 資料，請輸入已經雜湊過的資料。
   *
   * @param certFilter 憑證主旨過濾條件
   * @param pwd        憑證密碼
   * @param hash       簽章本文 hash 值
   * @param cbf        callback(code, msg, reserved, data)
   */
  capiSignPkcs1Hashed(certFilter, pwd, hash, cbf) {
    let cts = EncodingUtil.hexToBase64(hash);
    this.#capiSign('PKCS1_HASHED', certFilter, pwd, cts, cbf);
  }

  /**
   * [CAPI] 以用戶端憑軟體憑證簽署 PKCS7 資料。
   *
   * @param certFilter 憑證主旨過濾條件
   * @param pwd        憑證密碼
   * @param cts        簽章明文
   * @param cbf        callback(code, msg, reserved, data)
   */
  capiSignPkcs7(certFilter, pwd, cts, cbf) {
    this.#capiSign('PKCS7', certFilter, pwd, cts, cbf);
  }

    /**
   * [CAPI] 以用戶端憑軟體憑證簽署 PKCS7 資料。
   *
   * @param certFilter 憑證主旨過濾條件
   * @param pwd        憑證密碼
   * @param cts        簽章明文
   * @param cbf        callback(code, msg, reserved, data)
   */
    capiSignPkcs7Utf8(certFilter, pwd, cts, cbf) {
      this.#capiSign('PKCS7UTF8', certFilter, pwd, cts, cbf);
    }

  /**
   * [ATM] 檢查用戶端中介元件是否載入，若存在則產生元件雙向認證所需參數。
   *
   * @param cbf callback(code, msg, reserved, data)
   */
  atmCheck(cbf) {
    this.#agentCheck(AtlAgentTypeEnum.ATM, cbf);
  }

  /**
   * [ATM] 以中介元件驗證伺服器端的認證碼。
   *
   * @param serverAuthCode 伺服器認證碼
   * @param cbf            callback(code, msg, reserved, data)
   */
  atmServerAuth(serverAuthCode, cbf) {
    this.#agentServerAuth(AtlAgentTypeEnum.ATM, serverAuthCode, cbf);
  }

  /**
   * [ATM] 押碼。
   *
   * @param pwd        卡片密碼
   * @param memberNo   身分證字號
   * @param tacDateStr 系統產生押碼時間
   * @param un         系統押碼隨機值
   * @param cbf        callback(code, msg, reserved, data)
   */
  atmTac(pwd, memberNo, tacDateStr, un, cbf) {
    // 檢查參數
    if (!pwd || !memberNo || !tacDateStr || !un) {
      cbf('1185005', ErrorCode['1185005'], null, {});
      return;
    }

    // 連結卡片
    let connectSuccess = this.atmAgent.ConnectCard();
    if (!connectSuccess) {
      cbf('1186100', ErrorCode['1186100'], null, {});
      return;
    }

    // 讀取卡片資料
    let readSuccess = this.atmAgent.Read1001();
    if (!readSuccess) {
      cbf('1186110', ErrorCode['1186110'], null, {});
      this.atmAgent.DisConnectCard();
      return;
    }

    // 取得卡片資料
    let data1001 = this.atmAgent.Get1001();
    if (!data1001 || data1001.length < 372) {
      cbf('1186111', ErrorCode['1186111'], null, {});
      this.atmAgent.DisConnectCard();
      return;
    }

    // 解析 1001
    let bankNo = EncodingUtil.hexToAscii(data1001.substring(4, 10));
    let iccAppendix = data1001.substring(24, 84);

    // 特別處理，此種狀況不後送驗證，直接警告錯誤
    if (bankNo === '012') {
      if (iccAppendix === '003030303030303030303030303030303030303030303030303030303030') {
        cbf('1186112', ErrorCode['1186112'], null, {});
        this.atmAgent.DisConnectCard();
        return;
      }
    }

    // 驗證 PIN Code
    let pinSuccess = this.atmAgent.VerifyPin(pwd);
    if (!pinSuccess) {
      cbf('1186102', ErrorCode['1186102'], null, {});
      this.atmAgent.DisConnectCard();
      return;
    }

    // 開始押碼
    let tbt = un + tacDateStr.substr(8, 6);
    let success = this.atmAgent.TWNCH1(memberNo, tbt);
    if (!success) {
      cbf('1186105', ErrorCode['1186105'], null, {});
      this.atmAgent.DisConnectCard();
      return;
    }

    // 回傳成功結果
    let data = {
      twnch1Tac: this.atmAgent.GetTAC(),
      ef1001: data1001
    };

    cbf('0', ErrorCode['0'], null, data);
    this.atmAgent.DisConnectCard();
  }
}
